import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Call Sub Status master (Masters → Call Sub Statuses). A finer
 * breakdown of a Call Status, mirroring the QueryStatus →
 * QuerySubStatus pattern. The follow-up form cascades from the
 * picked parent CallStatus to its sub-statuses (e.g. parent
 * "Connected" → "Reached and spoke" / "Reached and quoted"; parent
 * "Not Connected" → "Busy" / "Voicemail" / "Switched off").
 *
 * `code` is unique WITHIN a parent (the composite uniqueness lives
 * on the DB), not globally. Description and sortOrder behave the
 * same as on the parent.
 */

/** Slim parent (call status) reference embedded in list/detail rows. */
const CallStatusRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});

const FollowupReasonRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});

const LostReasonRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});

export const CallSubStatusListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  callStatus: CallStatusRefSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** ADR-0051. When `true`, completing a follow-up with this
   *  sub-status triggers the auto-followup engine (21 / 3-per-day
   *  caps). The admin form uses this to skip showing the
   *  "next follow-up at" picker. */
  isAutoFollowup: z.boolean(),
  /** 2026-06-25 (was `isFollowUpReason`). When `true`:
   *  - the complete-follow-up dialog renders the FollowupReason dropdown
   *  - the server requires `followupReasonId` on the payload
   *  - the next-followup datetime is mandatory (operator must pick
   *    a future timestamp before submit). */
  isRequireFollowUp: z.boolean(),
  /** ADR-0053. When `true`, the complete-follow-up dialog renders
   *  the LostReason dropdown, the server requires `lostReasonId`,
   *  AND the lead is transitioned to LOST inside the same
   *  transaction. Mutually exclusive with `isAutoFollowup`. */
  isLostReason: z.boolean(),
  /** When `isAutoFollowup` is true, auto-fills follow-up reason on complete. */
  defaultFollowupReasonId: UuidSchema.nullable().optional(),
  defaultFollowupReason: FollowupReasonRefSchema.nullable().optional(),
  /** Reasons offered in the complete-follow-up dialog for this
   *  sub-status, in `sortOrder` then name order. An EMPTY array means
   *  UNRESTRICTED - the dialog falls back to every active reason. That
   *  is the pre-mapping behaviour, so existing rows are unaffected
   *  until an operator maps reasons here. */
  followupReasons: z.array(FollowupReasonRefSchema).default([]),
  /** Lost reasons offered when this sub-status closes the lead
   *  (`isLostReason`). Same contract as `followupReasons`: EMPTY means
   *  UNRESTRICTED, so the dropdown falls back to every active lost
   *  reason. */
  lostReasons: z.array(LostReasonRefSchema).default([]),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type CallSubStatusListItem = z.infer<typeof CallSubStatusListItemSchema>;

export const CallSubStatusListQuerySchema = PaginationQuerySchema.extend({
  callStatusId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type CallSubStatusListQuery = z.infer<typeof CallSubStatusListQuerySchema>;

export const CallSubStatusListResponseSchema = paginatedResponseSchema(CallSubStatusListItemSchema);
export type CallSubStatusListResponse = z.infer<typeof CallSubStatusListResponseSchema>;

export const CallSubStatusDetailSchema = CallSubStatusListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CallSubStatusDetail = z.infer<typeof CallSubStatusDetailSchema>;

const CallSubStatusCoreFieldsSchema = z.object({
  callStatusId: UuidSchema.describe(
    'Parent call status UUID. Use GET /call-statuses to populate the dropdown.',
  ),
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  /** ADR-0051. CMS-editable: when checked, completing a follow-up
   *  with this sub-status triggers the auto-followup engine. */
  isAutoFollowup: z.boolean().optional(),
  /** 2026-06-25 (was `isFollowUpReason`). CMS-editable: when
   *  checked, the dialog shows the FollowupReason dropdown AND
   *  requires a next-followup datetime; the server enforces both. */
  isRequireFollowUp: z.boolean().optional(),
  /** ADR-0053. CMS-editable: when checked, the complete-follow-up
   *  dialog renders the LostReason dropdown, the server requires
   *  `lostReasonId` AND auto-transitions the lead to LOST. */
  isLostReason: z.boolean().optional(),
  /** When `isAutoFollowup` is checked, default reason for auto chains. */
  defaultFollowupReasonId: UuidSchema.nullable().optional(),
  /** Reasons to offer in the complete-follow-up dialog for this
   *  sub-status. Replace-set semantics: the array sent REPLACES the
   *  stored mapping. Omit the key to leave the mapping untouched; send
   *  `[]` to clear it (back to unrestricted). When non-empty the server
   *  folds `defaultFollowupReasonId` in so the default is never a
   *  reason the operator cannot see. */
  followupReasonIds: z.array(UuidSchema).optional(),
  /** Lost reasons to offer when `isLostReason` is checked. Same
   *  replace-set semantics as `followupReasonIds`: omit to leave the
   *  mapping untouched, send `[]` to clear it back to unrestricted. */
  lostReasonIds: z.array(UuidSchema).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateCallSubStatusRequestSchema = CallSubStatusCoreFieldsSchema;
export type CreateCallSubStatusRequest = z.infer<typeof CreateCallSubStatusRequestSchema>;

export const UpdateCallSubStatusRequestSchema = CallSubStatusCoreFieldsSchema.partial();
export type UpdateCallSubStatusRequest = z.infer<typeof UpdateCallSubStatusRequestSchema>;
